import dayjs from "dayjs";

/**
 * Returns the route path to navigate to for a given notification, or null if the notification
 * should not be clickable (e.g., cancelled/deleted/rejected/batch notifications).
 *
 * @param {Object} notification - The notification object from the API
 * @param {string} notification.type - NotificationType enum value
 * @param {string|null} notification.dogId - UUID of the related dog
 * @param {string|null} notification.referenceId - UUID of the related entity
 * @param {string|null} notification.referenceDate - ISO datetime of the related event
 * @param {string} userType - "TRAINER" or "OWNER"
 * @returns {string|null} The route path to navigate to, or null if not clickable
 */
export function getNotificationRoute(notification, userType) {
    const { type, dogId, referenceId, referenceDate } = notification;

    // Build calendar query params from referenceDate
    const calendarDateParam = referenceDate
        ? `date=${dayjs(referenceDate).format("YYYY-MM-DD")}`
        : "";
    const calendarTimeParam = referenceDate
        ? `time=${dayjs(referenceDate).format("HH:mm")}`
        : "";
    const calendarParams = calendarDateParam && calendarTimeParam
        ? `?${calendarDateParam}&${calendarTimeParam}`
        : calendarDateParam ? `?${calendarDateParam}` : "";

    if (userType === "OWNER") {
        switch (type) {
            // Training notifications → calendar
            case "TRAINING_CONFIRMED":
            case "TRAINING_CREATED":
            case "TRAINING_REMINDED":
                return calendarParams ? `/owner/calendar${calendarParams}` : "/owner/calendar";

            // Group training → calendar (single training with date)
            case "GROUP_TRAINING_SCHEDULED":
            case "GROUP_TRAINING_UPDATED":
                return calendarParams ? `/owner/calendar${calendarParams}` : "/owner/calendar";

            // Exercise notifications → dog exercises tab
            case "EXERCISE_ASSIGNED":
            case "EXERCISE_COMPLETED":
                if (dogId) {
                    return `/owner/my-dog?dogId=${dogId}&tab=exercises`;
                }
                return "/owner/my-dog";

            // Summary → dog recommendations tab
            case "SUMMARY_ADDED":
                if (dogId) {
                    return `/owner/my-dog?dogId=${dogId}&tab=recommendations`;
                }
                return "/owner/my-dog";

            // Group member added → dog groups tab
            case "GROUP_MEMBER_ADDED":
                if (dogId) {
                    return `/owner/my-dog?dogId=${dogId}&tab=groups`;
                }
                return "/owner/my-dog";

            // Dog request approved → my dog page
            case "DOG_REQUEST_APPROVED":
                if (dogId) {
                    return `/owner/my-dog?dogId=${dogId}`;
                }
                return "/owner/my-dog";

            // Non-navigable notifications
            case "TRAINING_CANCELLED":
            case "GROUP_TRAINING_CANCELLED":
            case "GROUP_TRAINING_COMPLETED":
            case "DOG_REQUEST_REJECTED":
                return null;

            default:
                return null;
        }
    }

    if (userType === "TRAINER") {
        switch (type) {
            // Training requested → calendar with pending modal auto-open
            case "TRAINING_REQUESTED":
                if (referenceDate) {
                    const date = dayjs(referenceDate).format("YYYY-MM-DD");
                    const time = dayjs(referenceDate).format("HH:mm");
                    return `/calendar?date=${date}&time=${time}&pendingTime=${time}`;
                }
                return "/calendar";

            // Group training → calendar at specific date
            case "GROUP_TRAINING_SCHEDULED":
            case "GROUP_TRAINING_UPDATED":
                return calendarParams ? `/calendar${calendarParams}` : "/calendar";

            // Group member added → group details page
            case "GROUP_MEMBER_ADDED":
                if (referenceId) {
                    return `/trainer/group/${referenceId}`;
                }
                return "/trainer/groups";

            // Dog request submitted → dog details
            case "DOG_REQUEST_SUBMITTED":
                if (dogId) {
                    return `/dog/details/${dogId}`;
                }
                return "/podopieczni";

            // Exercise completed → dog exercises tab
            case "EXERCISE_COMPLETED":
                if (dogId) {
                    return `/dog/details/${dogId}?tab=exercises`;
                }
                return "/podopieczni";

            // Non-navigable trainer notifications
            case "TRAINING_CANCELLED":
            case "GROUP_TRAINING_CANCELLED":
            case "GROUP_TRAINING_COMPLETED":
                return null;

            default:
                return null;
        }
    }

    return null;
}

/**
 * Returns true if the notification type is one that should not be clickable
 * (i.e., relates to deletion/cancellation/rejection where there's nothing to navigate to).
 */
export function isNonNavigableNotification(type) {
    const nonNavigableTypes = [
        "TRAINING_CANCELLED",
        "GROUP_TRAINING_CANCELLED",
        "GROUP_TRAINING_COMPLETED",
        "DOG_REQUEST_REJECTED",
    ];
    return nonNavigableTypes.includes(type);
}
